import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Utensils, Dumbbell, Pill } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MorphingLandingVisual from '@/components/MorphingLandingVisual';
import ShareMenu from '@/components/ShareMenu';
import { LANDING } from '@/constants/testIds';

const features = [
  {
    icon: Utensils,
    title: 'Foods that heal, foods that harm',
    body: 'A personalized list of good and bad foods based on your age, weight and health story.',
  },
  {
    icon: Pill,
    title: 'A time-tested supplement stack',
    body: 'Guided by a 90-essential-nutrients philosophy. Dosages tuned to your body weight and health story.',
  },
  {
    icon: Dumbbell,
    title: 'Movement that fits your life',
    body: 'Home and gym workouts, plus stretching and mobility routines you can start today.',
  },
];

const Landing = () => (
  <div className="relative">
    {/* Hero */}
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F2EFE9] via-[#FAF9F6] to-[#FAF9F6]" />
        <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full bg-[#7E9979]/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C05746]/10 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-12 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="wellth-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E5E1D8] text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53] mb-8">
            <Sparkles className="w-3.5 h-3.5 text-[#7E9979]" strokeWidth={1.5} />
            Health is the greatest wealth
          </div>
          <h1 className="font-sans-body text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-[#1A2421] font-semibold">
            AI coach to build up your health in 60 seconds.
          </h1>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/intake">
              <Button
                data-testid={LANDING.startButton}
                className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-8 py-6 text-base h-auto gap-2 group"
              >
                Start my 60-second assessment
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} />
              </Button>
            </Link>
            <ShareMenu label="Send to a friend" testId={LANDING.shareButton} />
          </div>

          <p className="mt-8 text-lg text-[#4A5A53] leading-relaxed max-w-lg font-light">
            Answer four questions. Get a personalized plan &mdash; Diet, Nutrition, In-Home and Gym Workouts &mdash; rooted in a proven <em>90 essential nutrients</em> philosophy and delivered with the care of a friend who listens.
          </p>

          <div className="mt-10 flex items-center gap-6 text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">
            <span>Built for entrepreneurs</span>
            <span className="w-8 h-px bg-[#7E9979]/50" />
            <span>Trusted by whole households</span>
          </div>
        </div>

        <div className="flex justify-center lg:justify-end wellth-fade-up" style={{ animationDelay: '0.2s' }}>
          <MorphingLandingVisual />
        </div>
      </div>
    </section>

    {/* Features */}
    <section className="max-w-6xl mx-auto px-6 lg:px-8 py-20 sm:py-24">
      <div className="max-w-2xl mb-14">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">What you'll receive</span>
        <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#1A2421] font-medium leading-tight">
          Actionable steps — starting today.
        </h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-6">
        {features.map((f, i) => (
          <div
            key={i}
            className="bg-white border border-[#E5E1D8] rounded-2xl p-8 transition-all hover:-translate-y-1 hover:shadow-sm wellth-fade-up"
            style={{ animationDelay: `${0.1 * (i + 1)}s` }}
            data-testid={`feature-card-${i}`}
          >
            <div className="w-12 h-12 rounded-full bg-[#F2EFE9] flex items-center justify-center mb-6">
              <f.icon className="w-5 h-5 text-[#4A7C59]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-2xl text-[#1A2421] mb-3 font-medium">{f.title}</h3>
            <p className="text-[#4A5A53] leading-relaxed font-light">{f.body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Household strip */}
    <section className="border-t border-[#E5E1D8] bg-[#F2EFE9]/60">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">The household principle</span>
          <h2 className="mt-3 font-serif-display text-4xl text-[#1A2421] font-medium leading-tight">
            Everyone under one roof, on one plate.
          </h2>
          <p className="mt-5 text-[#4A5A53] leading-relaxed font-light">
            Cross-contamination isn&apos;t just bacteria &mdash; it&apos;s habits. When your household eats and moves together, your results compound. Wellth gives you a plan the whole family can share.
          </p>
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-[#E5E1D8] aspect-[4/3]">
          <img
            src="https://images.unsplash.com/photo-1540420773420-3366772f4999?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwb3JnYW5pYyUyMGZvb2QlMjBmcmVzaHxlbnwwfHx8fDE3ODMwMzY4MTJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Fresh organic vegetables"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="border-t border-[#E5E1D8]">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10 flex flex-col sm:flex-row gap-4 items-center justify-between text-sm text-[#4A5A53]">
        <span>© {new Date().getFullYear()} Wellth. Health is the greatest wealth.</span>
        <span className="italic font-light">Not medical advice. Consult your physician.</span>
      </div>
    </footer>
  </div>
);

export default Landing;
